import React, { useState, useEffect } from 'react';
import * as sharingUtils from '../utils/sharing';
import { useAuth } from '../contexts/AuthContext';
import ModalCloseButton from './ui/ModalCloseButton';

const ShareModal = ({ isOpen, onClose, planId, sidebarCollapsed = false }) => {
  const { isAuthenticated } = useAuth();
  const [shareType, setShareType] = useState('link'); // 'link' or 'email'
  const [email, setEmail] = useState('');
  const [shareToken, setShareToken] = useState('');
  const [shareUrl, setShareUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [permissions, setPermissions] = useState({
    can_view: true,
    can_edit_shopping_list: false,
    can_edit_theme: false,
  });
  const [shares, setShares] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [toastMessage, setToastMessage] = useState('');

  useEffect(() => {
    if (isOpen && planId && isAuthenticated) {
      loadShares();
    }
  }, [isOpen, planId, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadShares = async () => {
    try {
      const planShares = await sharingUtils.listPlanShares(planId);
      setShares(planShares);
    } catch (error) {
      console.error('Failed to load shares:', error);
    }
  };

  const handleCreateLink = async () => {
    // Check if there's already an active share link with the same permissions
    const activeLinks = shares.filter(s => s.share_type === 'link' && (!s.expires_at || new Date(s.expires_at) > new Date()));
    const matchingLink = activeLinks.find(link => 
      JSON.stringify(link.permissions || {}) === JSON.stringify(permissions)
    );
    
    if (matchingLink) {
      setConfirmDialog({
        title: 'Replace existing share link?',
        message: 'A share link with the same permissions already exists for this plan. Creating a new link will replace the existing one.',
        confirmText: 'Replace link',
        onConfirm: () => createShareLinkNow()
      });
      return;
    }

    await createShareLinkNow();
  };

  const createShareLinkNow = async () => {
    setLoading(true);
    setError('');

    try {
      const share = await sharingUtils.createShareLink(planId, permissions);
      setShareToken(share.share_token);
      setShareUrl(sharingUtils.getShareUrl(share.share_token));
      await loadShares();
    } catch (err) {
      const errorMsg = err.message || 'Failed to create share link';
      if (errorMsg.includes('credentials') || errorMsg.includes('401')) {
        setError('Authentication error. Please log out and log back in.');
      } else {
        setError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvite = async () => {
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await sharingUtils.createEmailInvite(planId, email, permissions);
      setEmail('');
      await loadShares();
      alert('Invitation sent!');
    } catch (err) {
      const errorMsg = err.message || 'Failed to send invitation';
      if (errorMsg.includes('credentials') || errorMsg.includes('401')) {
        setError('Authentication error. Please log out and log back in.');
      } else {
        setError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setToastMessage('Link copied to clipboard');
    setTimeout(() => setToastMessage(''), 2500);
  };

  const handleRevoke = async (token) => {
    setConfirmDialog({
      title: 'Revoke this share?',
      message: 'People with this link or invite will lose access immediately.',
      confirmText: 'Revoke share',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await sharingUtils.revokeShare(token);
          await loadShares();
          if (token === shareToken) {
            setShareToken('');
            setShareUrl('');
          }
        } catch (err) {
          setError(err.message || 'Failed to revoke share');
        }
      }
    });
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 sm:p-6"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[calc(100vh-32px)] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center shadow-lg">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Share Plan</h2>
              <p className="text-sm text-gray-500">Invite others to view or collaborate</p>
            </div>
          </div>
          <ModalCloseButton onClick={onClose} />
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Share Type Tabs */}
          <div className="w-full grid grid-cols-2 border-b border-gray-200">
            <button
              onClick={() => setShareType('link')}
              className={`min-w-0 px-2 sm:px-4 py-3 text-sm sm:text-base font-semibold text-center whitespace-nowrap border-b-2 transition ${
                shareType === 'link'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Anyone with link
            </button>
            <button
              onClick={() => setShareType('email')}
              className={`min-w-0 px-2 sm:px-4 py-3 text-sm sm:text-base font-semibold text-center whitespace-nowrap border-b-2 transition ${
                shareType === 'email'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Invite by email
            </button>
          </div>

          <div className="rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm leading-6 text-blue-950">
            {shareType === 'link'
              ? 'Link sharing means anyone who has the link can open this plan with the permissions you choose below.'
              : 'Email invites are intended for the people you add. Each invite is tied to that email address and the permissions below.'}
          </div>

          {/* Permissions */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">Permissions</label>
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={permissions.can_view}
                  onChange={(e) => setPermissions({ ...permissions, can_view: e.target.checked })}
                  className="w-4 h-4 text-primary rounded"
                />
                <span className="text-sm text-gray-700">Can view plan</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={permissions.can_edit_shopping_list}
                  onChange={(e) => setPermissions({ ...permissions, can_edit_shopping_list: e.target.checked })}
                  className="w-4 h-4 text-primary rounded"
                />
                <span className="text-sm text-gray-700">Can edit shopping list</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={permissions.can_edit_theme}
                  onChange={(e) => setPermissions({ ...permissions, can_edit_theme: e.target.checked })}
                  className="w-4 h-4 text-primary rounded"
                />
                <span className="text-sm text-gray-700">Can edit theme</span>
              </label>
            </div>
          </div>

          {/* Link Sharing */}
          {shareType === 'link' && (
            <div className="space-y-4">
              {!shareUrl ? (
                <button
                  onClick={handleCreateLink}
                  disabled={loading}
                  className="w-full px-4 py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primary-dark transition disabled:opacity-50"
                >
                  {loading ? 'Creating...' : 'Create Shareable Link'}
                </button>
              ) : (
                <div className="space-y-3">
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      value={shareUrl}
                      readOnly
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm w-full h-11"
                    />
                    <button
                      onClick={handleCopyLink}
                      className="px-4 py-2 h-11 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition w-full sm:w-auto shrink-0 flex items-center justify-center"
                    >
                      Copy
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">
                    Anyone with this link can open the plan. Editing depends on the permissions above.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Email Invite */}
          {shareType === 'email' && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@example.com"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary w-full h-11"
                />
                <button
                  onClick={handleSendInvite}
                  disabled={loading || !email}
                  className="px-6 py-2 h-11 bg-primary text-white font-semibold rounded-lg hover:bg-primary-dark transition disabled:opacity-50 w-full sm:w-auto shrink-0 flex items-center justify-center"
                >
                  {loading ? 'Sending...' : 'Send Invite'}
                </button>
              </div>
              <p className="text-xs leading-5 text-gray-500">
                Only the invited email is intended to use this access. Use a link instead when you want easy forwarding.
              </p>
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
              {error}
            </div>
          )}

          {/* Existing Shares */}
          {shares.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Active Shares</h3>
              <div className="space-y-2">
                {shares.map((share) => {
                  const shareUrl = share.share_type === 'link' ? sharingUtils.getShareUrl(share.share_token) : null;
                  const permText = share.permissions?.can_edit_shopping_list 
                    ? 'Can edit shopping list' 
                    : share.permissions?.can_edit_theme 
                    ? 'Can edit theme' 
                    : 'View only';
                  const permIcon = share.permissions?.can_edit_shopping_list || share.permissions?.can_edit_theme
                    ? <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                    : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>;
                  
                  return (
                    <div
                      key={share.share_token}
                      className="p-3 bg-gray-50 rounded-lg space-y-2"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {permIcon}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 whitespace-normal break-words">
                              {share.share_type === 'link' ? 'Shareable Link' : share.invited_email}
                            </p>
                            <p className="text-xs text-gray-500">{permText}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 self-end sm:self-auto shrink-0">
                          {shareUrl && (
                            <>
                              <button
                                onClick={() => {
                                  window.open(shareUrl, '_blank', 'noopener,noreferrer');
                                }}
                                className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded transition"
                                title="View link"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                              </button>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(shareUrl);
                                  alert('Link copied to clipboard!');
                                }}
                                className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded transition"
                                title="Copy link"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => handleRevoke(share.share_token)}
                            className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded transition"
                          >
                            Revoke
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
      {toastMessage && (
        <div className="pointer-events-none absolute bottom-6 left-1/2 z-[80] -translate-x-1/2 rounded-full bg-gray-900 px-4 py-2 text-sm font-semibold text-white shadow-lg">
          {toastMessage}
        </div>
      )}
      {confirmDialog && (
        <div className="absolute inset-0 z-[90] flex items-center justify-center bg-white/80 p-4 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-5 shadow-xl">
            <h3 className="text-lg font-extrabold text-gray-950">{confirmDialog.title}</h3>
            <p className="mt-2 text-sm leading-6 text-gray-600">{confirmDialog.message}</p>
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setConfirmDialog(null)}
                className="h-10 rounded-xl bg-gray-100 px-4 text-sm font-bold text-gray-700 transition hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  const action = confirmDialog.onConfirm;
                  setConfirmDialog(null);
                  await action?.();
                }}
                className={`h-10 rounded-xl px-4 text-sm font-bold text-white transition ${
                  confirmDialog.variant === 'danger' ? 'bg-red-600 hover:bg-red-700' : 'bg-primary hover:bg-primary-dark'
                }`}
              >
                {confirmDialog.confirmText || 'Continue'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShareModal;
